/**
 * Utility for parsing PostgreSQL array values that may come in various formats
 * from Supabase queries, realtime payloads, or PostgREST serialization.
 */

// UUID regex for validation
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID
 */
export const isUuid = (value: string): boolean => {
  return UUID_REGEX.test(value);
};

/**
 * Parse a value that could be:
 * - null/undefined → []
 * - string[] → return (trimmed)
 * - PostgreSQL array string like "{uuid1,uuid2}" or '{"uuid1","uuid2"}' → ["uuid1", "uuid2"]
 * - Empty PostgreSQL array "{}" → []
 * - Single scalar string (a UUID) → ["uuid"]
 * - Any other shape → []
 *
 * This handles the inconsistency between:
 * - Direct Supabase query results (usually string[])
 * - Realtime payloads (sometimes PostgreSQL array strings)
 * - PostgREST edge cases
 */
export const parsePgArray = (value: unknown): string[] => {
  // Handle null/undefined
  if (value == null) {
    return [];
  }

  // Handle arrays - but need to check if elements are valid
  if (Array.isArray(value)) {
    // Flatten any nested pg array strings within the array elements
    const result: string[] = [];
    for (const element of value) {
      if (typeof element === "string") {
        const trimmed = element.trim();
        // Check if this element is itself a pg array string like "{uuid1,uuid2}"
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          // Parse the pg array string
          const inner = trimmed.slice(1, -1);
          if (inner === "") {
            continue; // Empty array
          }
          // Split by comma, handling quoted values
          const parts = parsePgArrayString(inner);
          result.push(...parts);
        } else if (trimmed !== "") {
          result.push(trimmed);
        }
      } else if (element != null) {
        result.push(String(element).trim());
      }
    }
    return result.filter((x) => x !== "");
  }

  // Handle string values
  if (typeof value === "string") {
    const trimmed = value.trim();

    // Empty string
    if (trimmed === "") {
      return [];
    }

    // PostgreSQL array format: "{uuid1,uuid2}" or '{"uuid1","uuid2"}'
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      const inner = trimmed.slice(1, -1);
      if (inner === "") {
        return []; // Empty array "{}"
      }
      return parsePgArrayString(inner);
    }

    // Single scalar value (not wrapped in braces)
    return [trimmed];
  }

  // Unknown type - return empty
  return [];
};

/**
 * Parse the inner content of a PostgreSQL array string (without the outer braces).
 * Handles both quoted and unquoted values: uuid1,uuid2 or "uuid1","uuid2"
 */
const parsePgArrayString = (inner: string): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  let i = 0;

  while (i < inner.length) {
    const char = inner[i];

    if (char === '"' && !inQuotes) {
      inQuotes = true;
      i++;
      continue;
    }

    if (char === '"' && inQuotes) {
      // Check for escaped quote
      if (inner[i + 1] === '"') {
        current += '"';
        i += 2;
        continue;
      }
      inQuotes = false;
      i++;
      continue;
    }

    if (char === "," && !inQuotes) {
      const trimmed = current.trim();
      if (trimmed !== "") {
        result.push(trimmed);
      }
      current = "";
      i++;
      continue;
    }

    current += char;
    i++;
  }

  // Don't forget the last element
  const trimmed = current.trim();
  if (trimmed !== "") {
    result.push(trimmed);
  }

  return result;
};

/**
 * Validate and filter an array to only include valid UUIDs.
 * Use this before sending to RPC functions to prevent "invalid input syntax for type uuid" errors.
 */
export const filterValidUuids = (values: string[]): string[] => {
  const out: string[] = [];

  for (const raw of values) {
    if (raw == null) continue;

    // Some payloads arrive with literal quotes/braces in the string, and JSON escaping adds backslashes.
    // Example seen in errors: "{uuid... -> we must remove the backslashes before validating.
    const cleaned = String(raw).trim().replace(/\\/g, "").replace(/[{}"]/g, "");

    // If a single string contains multiple uuids (e.g. "uuid1,uuid2"), split it.
    for (const part of cleaned.split(",")) {
      const candidate = part.trim();
      if (candidate && isUuid(candidate)) out.push(candidate);
    }
  }

  // Deduplicate while preserving order
  return Array.from(new Set(out));
};

/**
 * Convenience function: parse a value and filter to only valid UUIDs.
 * This is the safest way to prepare UUID arrays for database operations.
 */
export const parseAndValidateUuidArray = (value: unknown): string[] => {
  const parsed = parsePgArray(value);
  return filterValidUuids(parsed);
};
